const GOOGLE_FONTS_BASE_URL = "https://fonts.googleapis.com/css2";

const loadedFontKeys = new Set<string>();

const ARABIC_FONT_FAMILIES = new Set<string>([
  "Amiri",
  "Changa",
  "El Messiri",
  "Harmattan",
  "Lateef",
  "Markazi Text",
  "Reem Kufi",
  "Scheherazade New",
  "Noto Sans Arabic",
  "Noto Naskh Arabic",
  "Noto Kufi Arabic",
  "IBM Plex Sans Arabic",
  "Cairo",
  "Tajawal",
  "Almarai",
]);

const normalizeFontFamilyName = (fontFamily: string): string => {
  // Extract just the font name, removing CSS fallbacks like "sans-serif", "serif", etc.
  // Handles formats like "Trirong, sans-serif" or "'Trirong', sans-serif" or just "Trirong"
  const normalized = String(fontFamily || "").trim();

  // Remove surrounding quotes
  const unquoted = normalized.replace(/^["']|["']$/g, "");

  // Split by comma and take the first part (the actual font name)
  const fontName = unquoted.split(",")[0].trim();

  // Remove quotes from the font name itself (in case of "'Trirong'")
  return fontName.replace(/^["']|["']$/g, "").trim();
};

const buildGoogleFontsHref = (fontFamily: string): string => {
  const family = normalizeFontFamilyName(fontFamily);
  const encoded = encodeURIComponent(family);
  const isArabic = ARABIC_FONT_FAMILIES.has(family);

  // Broad weight range; Google will ignore unsupported weights.
  const subset = isArabic ? "&subset=arabic" : "";
  return `${GOOGLE_FONTS_BASE_URL}?family=${encoded}:wght@300;400;500;600;700;800;900${subset}&display=swap`;
};

/**
 * Loads a single Google Font family by injecting a stylesheet link into <head>.
 * Safe to call multiple times; it is cached per fontFamily.
 */
export const ensureFontLoaded = async (fontFamily: string): Promise<void> => {
  if (typeof document === "undefined") return;

  const family = normalizeFontFamilyName(fontFamily);
  if (!family) return;

  // Skip loading "system" font stacks (contain commas).
  if (family.includes(",")) return;

  const key = family.toLowerCase();
  if (loadedFontKeys.has(key)) return;

  const linkId = `gf-${key.replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "")}`;
  if (document.getElementById(linkId)) {
    loadedFontKeys.add(key);
    return;
  }

  await new Promise<void>((resolve) => {
    const link = document.createElement("link");
    link.id = linkId;
    link.rel = "stylesheet";
    link.href = buildGoogleFontsHref(family);
    link.onload = () => resolve();
    link.onerror = () => resolve(); // non-fatal
    document.head.appendChild(link);
  });

  loadedFontKeys.add(key);
};

export const ensureFontsLoaded = async (fontFamilies: string[]): Promise<void> => {
  const unique = Array.from(
    new Set(fontFamilies.map((f) => normalizeFontFamilyName(f)).filter(Boolean))
  );
  await Promise.all(unique.map((f) => ensureFontLoaded(f)));
};

